import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  Check,
  CreditCard,
  Sparkles,
  Building2,
  Zap,
  Loader2,
} from "lucide-react";
import { TrialBanner } from "@/components/TrialBanner";

interface PricingPlan {
  id: string;
  name: string;
  price: number | null;
  price_display: string;
  description: string;
  features: string[];
  popular?: boolean;
  instance_limit: number;
  bob_limit: string;
}

/**
 * SoloBilling - Billing page for solo/direct customers
 *
 * Key features:
 * - Prepaid only (no net-30 terms)
 * - Pricing fetched from API (admin-configurable)
 * - Trial status display
 * - Stripe integration for payments
 */
export function SoloBilling() {
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch pricing plans from API
  const { data: plansData, isLoading: plansLoading, error: plansError } = useQuery({
    queryKey: ["pricing-plans", "solo"],
    queryFn: async () => {
      try {
        // Fetch solo pricing plans from API
        const response = await api.getSoloPricingPlans();
        return response.plans as PricingPlan[];
      } catch (err) {
        console.error("Error fetching pricing plans:", err);
        // Return empty array - UI will show "Contact sales" message
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  const plans = plansData || [];

  // Get current subscription info from user
  const currentPlan = (user as { subscription_tier?: string })?.subscription_tier || "trial";
  const isOnTrial = currentPlan === "trial";

  const handleSelectPlan = async (planId: string) => {
    setSelectedPlan(planId);
    setIsLoading(true);

    try {
      // Redirect to Stripe Checkout
      const response = await fetch("/api/billing/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan_id: planId }),
      });

      const data = await response.json();

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        console.error("No checkout URL returned");
      }
    } catch (error) {
      console.error("Error creating checkout session:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setIsLoading(true);

    try {
      // Redirect to Stripe Customer Portal
      const response = await fetch("/api/billing/create-portal-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      const data = await response.json();

      if (data.portal_url) {
        window.location.href = data.portal_url;
      }
    } catch (error) {
      console.error("Error creating portal session:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Trial Banner */}
      <TrialBanner />

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-100">Billing & Plans</h1>
        <p className="mt-2 text-slate-300">
          {isOnTrial
            ? "Choose a plan to continue after your trial ends"
            : "Manage your subscription and billing"}
        </p>
      </div>

      {/* Current Plan Status */}
      {!isOnTrial && (
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-100">Current Plan</h2>
              <p className="text-slate-300 mt-1 capitalize">
                {currentPlan} Plan
              </p>
            </div>
            <button
              onClick={handleManageBilling}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? "Loading..." : "Manage Billing"}
            </button>
          </div>
        </div>
      )}

      {/* Pricing Cards */}
      <div>
        <h2 className="text-xl font-semibold text-slate-100 mb-6">
          {isOnTrial ? "Choose Your Plan" : "Available Plans"}
        </h2>

        {/* Loading State */}
        {plansLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 text-blue-400 animate-spin" aria-hidden="true" />
            <span className="ml-3 text-slate-300">Loading plans...</span>
          </div>
        )}

        {/* Error State / No Plans */}
        {!plansLoading && (plansError || plans.length === 0) && (
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-8 text-center">
            <Building2 className="h-12 w-12 text-slate-500 mx-auto mb-4" aria-hidden="true" />
            <h3 className="text-lg font-semibold text-slate-100 mb-2">
              Pricing Coming Soon
            </h3>
            <p className="text-slate-400 mb-4">
              Our team is finalizing the pricing plans. Contact us for early access or custom pricing.
            </p>
            <a
              href="mailto:sales@aiia.io"
              className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
            >
              Contact Sales
            </a>
          </div>
        )}

        {/* Plans Grid */}
        {!plansLoading && plans.length > 0 && (
          <div className="grid gap-6 md:grid-cols-2 max-w-4xl">
            {plans.map((plan) => {
              const isCurrentPlan = currentPlan === plan.id;

              return (
                <div
                  key={plan.id}
                  className={`relative bg-slate-800/50 backdrop-blur-sm rounded-lg border p-6 ${
                    plan.popular
                      ? "border-blue-500"
                      : "border-slate-700"
                  }`}
                >
                  {/* Popular Badge */}
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-blue-500 text-white">
                        <Sparkles className="h-3 w-3" aria-hidden="true" />
                        Most Popular
                      </span>
                    </div>
                  )}

                  {/* Plan Header */}
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-blue-500/10 mb-4">
                      {plan.id === "starter" ? (
                        <Zap className="h-6 w-6 text-blue-400" aria-hidden="true" />
                      ) : (
                        <Building2 className="h-6 w-6 text-blue-400" aria-hidden="true" />
                      )}
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">
                      {plan.name}
                    </h3>
                    <p className="text-sm text-slate-400 mt-1">
                      {plan.description}
                    </p>
                  </div>

                  {/* Price */}
                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold text-slate-100">
                        {plan.price_display}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Billed monthly. Cancel anytime.
                    </p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span className="text-sm text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleSelectPlan(plan.id)}
                    disabled={isLoading || isCurrentPlan || plan.price === null}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                      isCurrentPlan
                        ? "bg-slate-700 text-slate-400 cursor-not-allowed"
                        : plan.price === null
                          ? "bg-slate-700 text-slate-400 cursor-not-allowed"
                          : plan.popular
                            ? "bg-blue-500 hover:bg-blue-600 text-white"
                            : "bg-slate-700 hover:bg-slate-600 text-white"
                    }`}
                  >
                    {isLoading && selectedPlan === plan.id
                      ? "Redirecting..."
                      : isCurrentPlan
                        ? "Current Plan"
                        : plan.price === null
                          ? "Contact Sales"
                          : isOnTrial
                            ? "Start Plan"
                            : "Switch to this plan"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Payment Methods Section */}
      {!isOnTrial && (
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <CreditCard className="h-5 w-5 text-slate-400" aria-hidden="true" />
            <h2 className="text-lg font-semibold text-slate-100">Payment Method</h2>
          </div>
          <p className="text-sm text-slate-400 mb-4">
            Manage your payment methods through Stripe's secure portal.
          </p>
          <button
            onClick={handleManageBilling}
            disabled={isLoading}
            className="text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
          >
            Update payment method →
          </button>
        </div>
      )}

      {/* FAQ / Help Section */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-slate-200">Can I upgrade or downgrade anytime?</h3>
            <p className="text-sm text-slate-400 mt-1">
              Yes, you can change your plan at any time. Changes take effect immediately and are prorated.
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-slate-200">What happens when my trial ends?</h3>
            <p className="text-sm text-slate-400 mt-1">
              You'll need to choose a plan to continue using AIIA. Your instances and data will be preserved for 30 days after trial expiration.
            </p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-slate-200">Do you offer refunds?</h3>
            <p className="text-sm text-slate-400 mt-1">
              We offer a 14-day money-back guarantee. If you're not satisfied, contact support for a full refund.
            </p>
          </div>
        </div>
      </div>

      {/* Contact Support */}
      <div className="text-center text-sm text-slate-400">
        <p>
          Need help choosing a plan?{" "}
          <a href="mailto:support@aiia.io" className="text-blue-400 hover:text-blue-300">
            Contact our support team
          </a>
        </p>
      </div>
    </div>
  );
}
